import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import ConfirmModal from './ConfirmModal';
import Loader from './Loader';
import './Auth.css';

/**
 * Public account deletion page (for Google Play Store compliance)
 * Route: /account/delete
 */
const DeleteAccount: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  useEffect(() => {
    // Check if user is authenticated
    const authenticated = apiService.refreshAuthState();
    setIsAuthenticated(authenticated);
  }, []);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteAccount();
      
      // deleteAccount() already calls forceLogout() internally
      // But we ensure tokens are cleared here too
      apiService.logout();
      
      showSuccess('Account Deleted', 'Your account has been deleted successfully.');
      
      // IMMEDIATE redirect - no delay to prevent any further API calls
      navigate('/login', { replace: true });
    } catch (error: any) {
      // If 401, account was deleted but we got unauthorized - still redirect
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        showSuccess('Account Deleted', 'Your account has been deleted successfully.');
        navigate('/login', { replace: true });
        return;
      }
      
      const appError = handleApiError(error);
      showError('Deletion Failed', appError.message);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Show loading while checking auth
  if (isAuthenticated === null || isDeleting) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <Loader message={isDeleting ? "Deleting your account..." : "Loading..."} size="large" />
        </div>
      </div>
    );
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <span className="logo-icon">💰</span>
              <h1>RokdaHisab</h1>
            </div>
            <h2>Delete Account</h2>
            <p>Please log in to delete your account</p>
          </div>

          <div className="auth-form">
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
                You need to be logged in to delete your account.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="auth-button"
                style={{ width: '100%' }}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated, show delete account UI
  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '600px' }}>
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">💰</span>
            <h1>RokdaHisab</h1>
          </div>
          <h2>Delete Account</h2>
          <p>Permanently delete your account and all associated data</p>
        </div>

        <div className="auth-form">
          <div className="delete-account-warning">
            <div className="warning-icon">⚠️</div>
            <h3>Warning: This action cannot be undone</h3>
            <p>
              Deleting your account will permanently remove:
            </p>
            <ul>
              <li>All your transactions and financial records</li>
              <li>All your accounts and balances</li>
              <li>All your reports and analytics</li>
              <li>Your profile and account settings</li>
            </ul>
            <p style={{ marginTop: '1rem', fontWeight: 600, color: '#dc3545' }}>
              This action is permanent and cannot be reversed.
            </p>
          </div>

          <button
            className="auth-button delete-account-button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            style={{
              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
              marginTop: '1.5rem'
            }}
          >
            Delete My Account
          </button>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button
              onClick={() => navigate('/app')}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#667eea', 
                textDecoration: 'none', 
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel and go back
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Confirm Account Deletion"
        message="This will permanently delete your account and all associated data. This action cannot be undone. Are you absolutely sure you want to proceed?"
        confirmLabel="Yes, Delete My Account"
        cancelLabel="Cancel"
        confirmVariant="danger"
        loading={isDeleting}
      />
    </div>
  );
};

export default DeleteAccount;

